import BaseApiService from './BaseApiService'
import type { CanonicalDocument } from '../cdm/types'

/** One source string and its state in a locale (TranslationCatalog.Rows on the server). */
export interface TranslationRow {
  key: string
  source: string
  occurrences: number
  location: string
  text: string | null
  status: 'missing' | 'draft' | 'accepted'
  inheritedFrom: string | null
  valid: boolean
  /** Every occurrence, with its per-occurrence translation in the requested locale (schema v33). */
  contexts?: OccurrenceRow[]
}

/** One occurrence of a string (TranslationCatalog.OccurrenceRow). */
export interface OccurrenceRow {
  /** Occurrence id, e.g. body/s0123456789abcdef#2: the nearest heading's key (or the part) and an ordinal. */
  context: string
  location: string
  /** e.g. Body under "Payment terms", #2 */
  label: string
  text: string | null
  status: 'draft' | 'accepted' | null
  valid: boolean
}

export interface LocaleSummary {
  locale: string
  accepted: number
  drafts: number
  missing: number
  orphaned: number
  staleOverrides?: number
}


/** One language Translate with AI offers (AiTranslationBilling.Candidates on the server). */
export interface AiLocaleCandidate {
  locale: string
  label: string
  /** The template already has this locale. */
  existing: boolean
  missing: number
  drafts: number
  /** Strings a run would send (missing + drafts). */
  strings: number
  repeatedStrings: number
  /** This locale's price in cents; the minimum charge applies to the selection's total. */
  amountCents: number
  /** Nothing to translate: costs nothing, not selectable. */
  upToDate: boolean
}

export interface AiQuoteList {
  currency: string
  minimumCents: number
  candidates: AiLocaleCandidate[]
}

export interface AiQuote extends AiQuoteList {
  quoteId: string
  expiresAt: string
  /** The total that will be charged at most. */
  amountCents: number
  subtotalCents: number
  minimumApplied: boolean
  strings: number
  repeatedStrings: number
  lines: Array<{ locale: string; label: string; strings: number; repeatedStrings: number; overridesSkipped: number; amountCents: number }>
}

export interface AiLocaleResult {
  locale: string
  failed: boolean
  error: string | null
  requested: number
  repeatedStrings: number
  drafts: Array<{ key: string; text: string }>
  discarded: string[]
  /** Per-occurrence drafts, for strings that occur more than once and need other wording in one place. */
  overrideDrafts: Array<{ key: string; context: string; text: string }>
  /** Per-occurrence drafts discarded because they changed a {{variable}} or tag. */
  overridesDiscarded: Array<{ key: string; context: string }>
  /** Repeated strings sent without occurrence context because of the per-request cap. */
  overridesSkipped: number
  charged: boolean
}

export interface AiDraftResult {
  results: AiLocaleResult[]
  chargedCents: number
  quotedCents: number
  currency: string
}

/**
 * /v1/translations. The editor works on its unsaved document (inspect, aiQuote/aiDraft with the CDM) and saves
 * translations with the rest of the document; the version-based endpoints are the public API surface.
 */
class TranslationsApiService extends BaseApiService {
  /** Strings of an unsaved document, with their state in `locale` when given. */
  inspect(doc: CanonicalDocument, locale?: string, signal?: AbortSignal): Promise<{ strings: TranslationRow[]; locales: LocaleSummary[] }> {
    return this.request('POST', '/v1/translations/inspect', { canonicalJson: JSON.stringify(doc), locale: locale || undefined }, false, false, signal)
  }

  /** Translate with AI, step 1: the languages on offer and each one's price. Never calls the model. */
  aiLanguages(doc: CanonicalDocument, templateId?: string): Promise<AiQuoteList> {
    return this.post('/v1/translations/ai-quote', { canonicalJson: JSON.stringify(doc), templateId: templateId || undefined })
  }

  /** Translate with AI, step 2: a fixed-price, single-use quote for exactly `locales`. Never calls the model. */
  aiQuote(doc: CanonicalDocument, locales: string[], templateId?: string): Promise<AiQuote> {
    return this.post('/v1/translations/ai-quote', { canonicalJson: JSON.stringify(doc), templateId: templateId || undefined, locales })
  }

  /** Translate with AI, step 3: runs a confirmed quote on the unsaved document. Nothing is saved; drafts come back. */
  aiDraft(doc: CanonicalDocument, quoteId: string, locales: string[]): Promise<AiDraftResult> {
    return this.post('/v1/translations/ai-draft', { canonicalJson: JSON.stringify(doc), quoteId, locales })
  }
}

export const translationsApiService = new TranslationsApiService()
